import axios from 'axios';

/**
 * `image` may be null (text-to-video) as well as a base64 string
 * (image-to-video). VideoTable.vue's popover-driven single-scene Animate
 * flow always sends a captured scene snapshot. Its batch AI Scenes flow
 * (POR-13874) sends null for a scene that has nothing to draw from, and a
 * letterboxed data URI (see letterbox-image.js) for a scene whose storyboard
 * entry carries a script-inserted product image (POR-13874-2) — the backend
 * swaps in a different prompt suffix depending on which one it gets, see
 * AiMotionPresets::promptFor's $hasImage parameter.
 */
export const startAiMotion = async ({
  image,
  preset,
  customPrompt,
  clipLength,
  width,
  height,
  resolution,
  sceneNumber,
  projectName,
}) => {
  const { data } = await axios.post('/video/ai-motion', {
    image,
    preset,
    custom_prompt: customPrompt || null,
    clip_length: clipLength,
    width,
    height,
    // Optional (POR-14117). Omitted by the single-scene Animate popover, which
    // the backend then defaults to VeoRequest::DEFAULT_RESOLUTION; AI Scenes
    // sends the resolution chosen in VideoBriefScriptModal. `aspectRatio` needs
    // no equivalent — VeoRequest derives it from the width/height above.
    resolution: resolution || null,
    scene_number: sceneNumber,
    project_name: projectName || null,
  });

  return data;
};

export const fetchAiMotionBalance = async () => {
  const { data } = await axios.get('/video/ai-motion/balance');

  return data.credits_remaining;
};

// Mirrors AiMotionCreditLog::STATUS_* (app/Domains/Auth/Models/AiMotionCreditLog.php).
export const AI_MOTION_STATUS_PENDING = 'pending';
export const AI_MOTION_STATUS_SUCCEEDED = 'succeeded';
export const AI_MOTION_STATUS_FAILED = 'failed';

/**
 * Catch-up for VideoTable.vue's rehydrateAiMotionPendingClips (POR-13874): asks, for a
 * batch of uuids that just got rehydrated from Ai_Motion_Pending markers, whether any of
 * them already resolved while nobody was subscribed to hear the `.ai-motion-finished` /
 * `.ai-motion-failed` broadcast (the AI Scenes reload-window bug this exists to close).
 * `uuids` is capped server-side (AiMotionStatusRequest) — callers should already be
 * passing at most a project's worth of pending markers, not an unbounded list.
 */
export const fetchAiMotionStatus = async (uuids) => {
  const { data } = await axios.post('/video/ai-motion/status', { uuids });

  return data.clips;
};
